import _ from 'lodash'
import { sendPageview, createI13nNode, sendI13nEvent } from '@vzmi/ec-react-i13n'
import { i13nConf, updateListingProposalI13nConf, batchUpdateListingProposalI13nConf, batchUploadI13nConf } from '../../conf/i13nConf'
import { matchPath } from 'react-router'
import {
  matchHash
} from '../app/lib/react-hash-route'
import 'custom-event-polyfill'
import { PROPOSAL_PHASE_TYPE, PROPOSAL_TYPE } from '@vzmi/shp_shared_compo/constants/proposal'
import { PAGE_FLOW } from '../app/constants/proposalTypes'
import { COMMA } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'

export const retrieveRouteByLocation = ({ location, routes }) => {
  const localLocation = !location && global.window ? window.location : location
  const findRoute = (route) => {
    if (localLocation.hash) {
      return matchHash({ location: localLocation, ...route })
    } else {
      // if it's not supported by hash route, match with matchPath
      // https://github.com/ReactTraining/react-router/blob/master/packages/react-router/docs/api/matchPath.md
      return matchPath(localLocation.pathname, { exact: route.path, path: route.path })
    }
  }

  const diveIntoRoutes = (localRoutes) => {
    let returnRoute
    _.some(localRoutes, (route) => {
      const foundRoute = findRoute(route)
      if (foundRoute) {
        returnRoute = route
        return true
      }
      const nestedRoutes = _.get(route, 'routes', null)
      if (!_.isEmpty(nestedRoutes)) {
        const hasFoundInNestedRoutes = diveIntoNestedRoutes(nestedRoutes)
        if (hasFoundInNestedRoutes) {
          returnRoute = hasFoundInNestedRoutes
        }
        return hasFoundInNestedRoutes
      }
    })
    return returnRoute
  }

  const diveIntoNestedRoutes = (nestedRoutes) => {
    let returnRoute = false
    _.some(nestedRoutes, (route) => {
      const foundRoute = findRoute(route)
      if (foundRoute) {
        returnRoute = route
        return true
      }
      return false
    })
    return returnRoute
  }

  if (_.isEmpty(routes)) {
    // fallback to server routes
    const serverRoutes = require('../app/routes').default
    return diveIntoRoutes(serverRoutes)
  }
  return diveIntoRoutes(routes)
}

export const retrievePageI13n = ({ pageKey, ...props }, customizeI13nConfig) => {
  const i13nData = _.get(customizeI13nConfig || i13nConf, [pageKey], null)
  if (i13nData) {
    const {
      dynamicDataCallback,
      ...staticData
    } = i13nData

    const dynamicData = dynamicDataCallback
      ? dynamicDataCallback(props)
      : {}

    return {
      ...staticData,
      ...dynamicData
    }
  }

  console.error(`retrievePageI13n error, page i13n data not found. PageKey: ${pageKey},`, props)
}

export const sendI13nPageView = (i13nData) => {
  let options
  if (i13nData) {
    options = _.pick(i13nData, 'page')
    sendPageview(i13nData, options)
  }
}

export const sendI13nClickEvent = ({ i13n, i13nModel, e }) => {
  const event = e || new window.CustomEvent('click')
  const { category, action, label } = i13nModel
  sendI13nEvent({
    i13n,
    i13nModel: {
      ...i13nModel,
      gtmDataLayer: {
        event: 'ec_click_event',
        click_category: category,
        click_action: action,
        click_label: label
      }
    },
    e: event
  })
}

export const I13nSection = createI13nNode('section', {
  isLeafNode: false,
  bindClickEvent: false
})

export const booleanToNum = boolean => boolean ? 1 : 0 // 1:有勾,0:沒勾(同時修改)

export const currentPageFlowPhase = (pageFlow) => {
  if (pageFlow.isOverviewPage) { return }
  let phase
  switch (true) {
    case pageFlow.isEditPage:
      phase = PROPOSAL_PHASE_TYPE.COMPOSE
      break
    case pageFlow.isConfirmPage:
      phase = PROPOSAL_PHASE_TYPE.CONFIRM
      break
    case pageFlow.isSuccessPage:
      phase = PAGE_FLOW.SUCCESS
      break
    default:
      break
  }
  return phase
}

export const singleSendI13nPageViewByPageKey = (sendI13nPageViewByPageKey, proposalType, phase, formValues) => {
  const {
    updateListingAttributeVisible,
    updateVideoVisible,
    updateCopyVisible
  } = formValues
  const pageKey = `${proposalType}-${phase}`
  // 在修改賣場資訊confirm頁 i13n config 插入updateAttribute(cat4Name: dimension6),updateVideo(cat5Name: dimension7),updateCopy(cat6Name: dimension8)
  // https://git.vzbuilders.com/EC-Web/ec-react-i13n/blob/master/src/mappingConf.js

  if (proposalType === PROPOSAL_TYPE.UPDATE_LISTING && phase === PROPOSAL_PHASE_TYPE.CONFIRM) { // 只有在confirm 插入dimension6,dimension7,dimension8
    const i13nConfigWithConfirmIsChecked = {
      ...updateListingProposalI13nConf,
      [pageKey]: {
        ..._.get(updateListingProposalI13nConf, pageKey, {}),
        cat4Name: booleanToNum(updateListingAttributeVisible),
        cat5Name: booleanToNum(updateVideoVisible),
        cat6Name: booleanToNum(updateCopyVisible)
      }
    }
    sendI13nPageViewByPageKey(pageKey, i13nConfigWithConfirmIsChecked)
  } else {
    sendI13nPageViewByPageKey(pageKey, updateListingProposalI13nConf)
  }
}

export const batchSendI13nPageViewByPageKey = (sendI13nPageViewByPageKey, proposalType, queryStringListingIds, phase, postfix, reservation = '') => {
  const pageKey = `${proposalType}-${phase}-${postfix}`
  // 在預設靜態批次修改系列的 i13n config 插入賣場數(cat3Name: dimension5)
  // https://git.vzbuilders.com/EC-Web/ec-react-i13n/blob/master/src/mappingConf.js
  const i13nConfigWithListingsCount = {
    ...batchUpdateListingProposalI13nConf,
    ...batchUploadI13nConf,
    [pageKey]: {
      ..._.get(batchUpdateListingProposalI13nConf, pageKey, {}),
      ..._.get(batchUploadI13nConf, pageKey, {}),
      ...!_.isEmpty(queryStringListingIds)
        ? {
          cat3Name: _.flow(
            idsStr => _.split(idsStr, COMMA),
            _.size
          )(queryStringListingIds)
        }
        : {},
      // dimension12(cat4Id) 批次檔案上傳紀錄是否預約修改與回檔
      ...!_.isEmpty(reservation)
        ? {
          cat4Id: reservation
        }
        : {}
    }
  }
  sendI13nPageViewByPageKey(pageKey, i13nConfigWithListingsCount)
}
